import { useMemo, useRef, useState } from "react";

import { AlertCircle, ArrowRight, CheckCircle, Gauge, Globe, Info, X, XCircle } from "lucide-react";

import LoadingSpinner from "./LoadingSpinner";

const categories = ["All", "Essential", "Elevating", "Enhancing"];

// --- CIRCULAR PROGRESS COMPONENT ---
const CircularProgress = ({ score }) => {
  const radius = 80;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  const getScoreColor = (score) => {
    if (score >= 80) return "#059669";
    if (score >= 60) return "#d97706";
    if (score >= 40) return "#ea580c";
    return "#dc2626";
  };

  return (
    <div className="relative w-48 h-48 mx-auto">
      <svg className="w-48 h-48 transform -rotate-90" viewBox="0 0 200 200">
        <circle
          cx="100"
          cy="100"
          r={radius}
          stroke="#e2e7ff"
          strokeWidth="12"
          fill="none"
          className="opacity-30"
        />
        <circle
          cx="100"
          cy="100"
          r={radius}
          stroke={getScoreColor(score)}
          strokeWidth="12"
          fill="none"
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-out"
          style={{
            // Dynamic shadow color based on score
            filter: score >= 80 ? "drop-shadow(0 0 6px rgba(16, 185, 129, 0.4))" : "none",
          }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="text-4xl font-bold mb-1 text-on-surface">{score}</div>
          <div className="text-sm font-medium text-on-surface-variant">/ 100</div>
        </div>
      </div>
    </div>
  );
};

// --- STATUS BADGE COMPONENT ---
const StatusBadge = ({ status }) => {
  // Styles are already dynamic/specific to status and look good in both modes
  const getStatusStyles = (status) => {
    switch (status) {
      case "Fail":
        return "bg-red-500/20 text-red-400 border-red-500/30";
      case "Low":
        return "bg-orange-500/20 text-orange-400 border-orange-500/30";
      case "Medium":
        return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "Pass":
      case "High":
        return "bg-green-500/20 text-green-400 border-green-500/30";
      default:
        return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "Fail":
        return <XCircle className="w-3 h-3" />;
      case "Low":
        return <AlertCircle className="w-3 h-3" />;
      case "Medium":
        return <Info className="w-3 h-3" />;
      case "Pass":
      case "High":
        return <CheckCircle className="w-3 h-3" />;
      default:
        return <Info className="w-3 h-3" />;
    }
  };

  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full border ${getStatusStyles(
        status,
      )}`}
    >
      {getStatusIcon(status)}
      {status}
    </span>
  );
};

// --- SIGNAL CARD COMPONENT ---
const SignalCard = ({ signal, onClick }) => {
  return (
    <div
      onClick={onClick}
      className="rounded-lg p-4 transition-all duration-200 cursor-pointer backdrop-blur-sm bg-surface-container-lowest border border-surface-container-high hover:border-primary/30 hover:bg-surface-container-low"
    >
      <div className="flex items-start justify-between mb-3">
        <h3 className="font-medium text-sm leading-tight flex-1 pr-2 text-on-surface">
          {signal.name}
        </h3>
        <StatusBadge status={signal.status} />
      </div>
      <div className="flex items-center justify-between">
        <div className="text-2xl font-bold text-on-surface">
          {signal.score}
          <span className="text-sm font-normal ml-1 text-on-surface-variant">/ 100</span>
        </div>
        <div className="text-xs px-2 py-1 rounded bg-surface-container-high text-on-surface-variant">
          {signal.category}
        </div>
      </div>
    </div>
  );
};

// --- MODAL COMPONENT ---
const Modal = ({ signal, onClose }) => {
  if (!signal) return null;

  const getScoreColor = (score) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-amber-600";
    if (score >= 40) return "text-orange-600";
    return "text-red-600";
  };

  const getRecommendation = (status) => {
    switch (status) {
      case "Fail":
        return "Immediate action required. This is a critical trust factor that needs urgent attention.";
      case "Low":
        return "Improvement needed. Consider prioritizing enhancements to boost user trust.";
      case "Medium":
        return "Good foundation. Small optimizations could yield significant trust improvements.";
      case "Pass":
      case "High":
        return "Excellent performance. Maintain current standards and monitor for any changes.";
      default:
        return "Review current implementation and identify optimization opportunities.";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto bg-surface-container-lowest border border-surface-container-high shadow-cloud">
        <div className="flex items-center justify-between p-6 border-b border-surface-container-high">
          <h2 className="text-xl font-semibold text-on-surface">{signal.name}</h2>
          <button
            onClick={onClose}
            className="transition-colors p-1 text-on-surface-variant hover:text-on-surface"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <StatusBadge status={signal.status} />
            <div className={`text-2xl font-bold ${getScoreColor(signal.score)}`}>
              {signal.score}/100
            </div>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2 text-on-surface-variant">Description</h3>
            <p className="text-on-surface-variant leading-relaxed">{signal.description}</p>
          </div>

          <div>
            <h3 className="text-sm font-medium mb-2 text-on-surface-variant">Recommendation</h3>
            <p className="text-on-surface-variant leading-relaxed">
              {getRecommendation(signal.status)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// --- UPDATES VIEW COMPONENT (Main Dashboard) ---
// Presentational only: Report.jsx owns the fetch-once-per-auditId lifecycle
// (mirroring how it holds the audit report itself) so the scorecard survives
// switching away from this tab and back, instead of refetching every time.
function UpdatesView({ auditData, isAnalyzing, error }) {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSignal, setSelectedSignal] = useState(null);
  const signalsSectionRef = useRef(null);

  const handleSelectCategory = (category) => {
    setSelectedCategory(category);
    signalsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const filteredSignals = useMemo(() => {
    if (!auditData?.signals) return [];

    const signals =
      selectedCategory === "All"
        ? auditData.signals
        : auditData.signals.filter((signal) => signal.category === selectedCategory);

    return signals.sort((a, b) => {
      // Sort by score ascending (lowest score first)
      if (a.score !== b.score) return a.score - b.score;
      // Then by name
      return a.name.localeCompare(b.name);
    });
  }, [selectedCategory, auditData]);

  const categoryStats = useMemo(() => {
    if (!auditData || !auditData.signals) return [];

    return categories.slice(1).map((category) => {
      const categorySignals = auditData.signals.filter((s) => s.category === category);
      const avgScore =
        categorySignals.length > 0
          ? Math.round(
              categorySignals.reduce((sum, s) => sum + s.score, 0) / categorySignals.length,
            )
          : 0;
      return { category, avgScore, count: categorySignals.length };
    });
  }, [auditData]);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="bg-surface-container-lowest/90 backdrop-blur border-b border-surface-container-high sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <h1 className="text-3xl font-bold text-on-surface text-center mb-2">
            🤖 AI Trust Signals Scorecard
          </h1>
          {auditData?.url && (
            <p className="text-center text-sm text-on-surface-variant">
              Analyzing: <span className="text-primary font-medium">{auditData.url}</span>
            </p>
          )}

          {/* Error Message */}
          {error && (
            <div className="max-w-2xl mx-auto mt-3">
              <div className="bg-red-500/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg flex items-center gap-2">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {isAnalyzing ? (
          <div className="text-center py-20">
            <div className="backdrop-blur rounded-2xl p-12 max-w-2xl mx-auto bg-surface-container-lowest shadow-cloud border border-surface-container-high">
              <LoadingSpinner size="lg" message="Analyzing trust signals..." />
            </div>
          </div>
        ) : auditData ? (
          <>
            {/* Overall Score Section */}
            <div className="text-center mb-12">
              <div className="backdrop-blur rounded-2xl p-8 mb-6 shadow-xl bg-surface-container-lowest shadow-cloud border border-surface-container-high">
                <h2 className="text-3xl font-bold mb-6 flex items-center justify-center gap-2 text-on-surface">
                  <Gauge className="w-6 h-6 text-primary" />
                  Overall Trust Score
                </h2>
                <CircularProgress score={auditData.overallScore} />
                <p className="mt-6 max-w-md mx-auto text-sm text-on-surface-variant">
                  This score aggregates performance across all 19 trust signals.
                </p>
              </div>

              {/* Category Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {categoryStats.map((stat) => (
                  <button
                    key={stat.category}
                    type="button"
                    onClick={() => handleSelectCategory(stat.category)}
                    className="backdrop-blur rounded-xl p-5 shadow-md text-left transition-transform hover:scale-[1.02] bg-surface-container-lowest shadow-cloud border border-surface-container-high focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <h3 className="text-xl font-semibold mb-1 text-on-surface">{stat.category}</h3>
                    <p className="text-sm mb-3 text-on-surface-variant">{stat.count} Signals</p>
                    <div className="flex items-center justify-between">
                      <span className="text-4xl font-extrabold text-on-surface">
                        {stat.avgScore}
                      </span>
                      <ArrowRight className="w-6 h-6 text-primary" />
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Category Tabs */}
            <div
              ref={signalsSectionRef}
              className="flex flex-wrap gap-2 mb-8 justify-center scroll-mt-24"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-200 ${
                    selectedCategory === category
                      ? "bg-gradient-to-br from-primary to-primary-container text-on-primary shadow-lg shadow-primary/20"
                      : "bg-surface-container-low text-on-surface-variant hover:bg-surface-container-high border border-surface-container-high"
                  }`}
                >
                  {category}{" "}
                  {category !== "All" &&
                    `(${auditData.signals.filter((s) => s.category === category).length})`}
                </button>
              ))}
            </div>

            {/* Signals Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSignals.map((signal, index) => (
                <SignalCard
                  key={`${signal.name}-${index}`}
                  signal={signal}
                  onClick={() => setSelectedSignal(signal)}
                />
              ))}
            </div>

            {/* Modal */}
            <Modal signal={selectedSignal} onClose={() => setSelectedSignal(null)} />
          </>
        ) : (
          <div className="text-center py-20">
            <div className="backdrop-blur rounded-2xl p-12 max-w-2xl mx-auto bg-surface-container-lowest shadow-cloud border border-surface-container-high">
              <Globe className="w-20 h-20 text-primary mx-auto mb-6" />
              <h2 className="text-2xl font-bold mb-4 text-on-surface">No Analysis Available</h2>
              <p className="text-on-surface-variant">
                Please start an audit from the home page to view trust signals analysis.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UpdatesView;
